// React imports
import React, { useEffect, useState } from "react";

// External library imports
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import { getAllDesignations } from "../../../Service/Apis";
import { Designation } from "./Types/DesignationTypes";

const DesignationsList: React.FC = () => {
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const showToast = useCustomToast();

  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  // Function declarations
  const handleShowToast = (
    message: string,
    variant: ToastVariant,
    isCloseable: boolean
  ) => {
    showToast(message, variant, isCloseable);
  };

  const fetchDesignations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await dispatch(fetchData(getAllDesignations));
      if (response && Array.isArray(response)) {
        setDesignations(response);
      } else {
        setDesignations([]);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load designations. Please try again.";
      setError(errorMessage);
      handleShowToast(
        errorMessage,
        "error",
        true
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKRA = (designationId: string, designationName: string) => {
    navigate(`/pms/kra/create/${designationId}`, {
      state: { 
        designationId,  // Pass as backup in state too
        designationName 
      },
    });
  };

  const handleViewTemplates = (designationId: string) => {
    navigate(`/pms/kra/library?designation=${designationId}`);
  };

  // useEffect hooks
  useEffect(() => {
    fetchDesignations();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 10, mb: 5 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchDesignations}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 5 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: "700", color: "text.primary", mb: 1 }}
        >
          Roles
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1rem" }}>
          Select a Role to create KRA
        </Typography>
      </Box>

      {designations.length === 0 ? (
        <Alert severity="info">
          No designations found. Please add designations from the admin panel.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {designations.map((designation) => (
            <Grid item xs={12} sm={6} md={4} key={designation.id}>
              <Card
                elevation={3}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                    borderColor: "primary.light",
                  },
                  border: "2px solid",
                  borderColor: "grey.200",
                  borderRadius: 2,
                  backgroundColor: "background.paper",
                }}
              >
                <CardContent sx={{ flexGrow: 1, pb: 2 }}>
                  {/* Designation Header with Icon */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        mt: 0.5,
                        color: "primary.main",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <WorkOutlineIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "600",
                        color: "text.primary",
                        wordBreak: "break-word",
                        lineHeight: 1.3,
                      }}
                    >
                      {designation.designation_name}
                    </Typography>
                  </Box>

                  {/* Description */}
                  {designation.designation_description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, minHeight: "40px" }}
                    >
                      {designation.designation_description}
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0, mt: "auto" }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() =>
                      handleCreateKRA(
                        designation.id,
                        designation.designation_name
                      )
                    }
                    sx={{
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: "600",
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: 2,
                      },
                    }}
                  >
                    Create KRAs and KPIs
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default DesignationsList;
